import { Duration, durationScalarMs, DurationUnit, SolrUnit } from '@fullstory/search-types';
import { assertExhaustive } from '@fullstory/typeutils';

export const knownSolrUnits = ['MILLI', 'SECOND', 'MINUTE', 'HOUR', 'DAY']; // @VisibleForTesting

function _toMilli(d: Duration): number {
  switch (d.unit) {
    case DurationUnit.milli:
      return d.scalar;
    case DurationUnit.second:
      return 1000 * _toMilli({ unit: DurationUnit.milli, scalar: d.scalar });
    case DurationUnit.minute:
      return 60 * _toMilli({ unit: DurationUnit.second, scalar: d.scalar });
    case DurationUnit.hour:
      return 60 * _toMilli({ unit: DurationUnit.minute, scalar: d.scalar });
    case DurationUnit.day:
      return 24 * _toMilli({ unit: DurationUnit.hour, scalar: d.scalar });
    default:
      throw assertExhaustive(d.unit, 'unhandled duration unit');
  }
}

function _toSecond(d: Duration): number {
  switch (d.unit) {
    case DurationUnit.milli:
      return _toSecond({ unit: DurationUnit.second, scalar: d.scalar }) / 1000;
    case DurationUnit.second:
      return d.scalar;
    case DurationUnit.minute:
      return 60 * _toSecond({ unit: DurationUnit.second, scalar: d.scalar });
    case DurationUnit.hour:
      return 60 * _toSecond({ unit: DurationUnit.minute, scalar: d.scalar });
    case DurationUnit.day:
      return 24 * _toSecond({ unit: DurationUnit.hour, scalar: d.scalar });
    default:
      throw assertExhaustive(d.unit, 'unhandled duration unit');
  }
}

// Search queries error if the millisecond duration is not a whole number.
export function toMilli(d: Duration): number {
  return Math.round(_toMilli(d));
}

export function toSecond(d: Duration): number {
  return Math.round(_toSecond(d));
}

export function fromMilli(ms: number, minUnit: DurationUnit = DurationUnit.milli): Duration {
  let best: Duration | null = null;
  const maybeUpdateBest = (val: Duration) => {
    if (
      (val.scalar >= 1 || val.unit == minUnit) &&
      durationScalarMs.get(val.unit) >= durationScalarMs.get(minUnit)
    ) {
      // eligible to be used
      // ignoring '.' characters, take the value with the least digits for simplicity
      if (
        best == null ||
        val.scalar.toString().replace('.', '').length <
          best.scalar.toString().replace('.', '').length
      ) {
        best = val;
      }
    }
  };

  maybeUpdateBest({ unit: DurationUnit.day, scalar: ms / durationScalarMs.get(DurationUnit.day) });
  maybeUpdateBest({
    unit: DurationUnit.hour,
    scalar: ms / durationScalarMs.get(DurationUnit.hour),
  });
  maybeUpdateBest({
    unit: DurationUnit.minute,
    scalar: ms / durationScalarMs.get(DurationUnit.minute),
  });
  maybeUpdateBest({
    unit: DurationUnit.second,
    scalar: ms / durationScalarMs.get(DurationUnit.second),
  });
  maybeUpdateBest({ unit: DurationUnit.milli, scalar: ms });

  return best ?? { unit: minUnit ?? DurationUnit.milli, scalar: 0 };
}

export function fromSecond(sec: number): Duration {
  return fromMilli(sec * 1000);
}

const solrUnitsGroup = '(' + knownSolrUnits.join('|') + ')';

// Matches a Solr time string.
// It expects the unit in the base and adjust portions to be the same. See the "\1" backreference.
//
// In literal form:
//   /^NOW/(MILLI|SECOND|MINUTE|HOUR|DAY)(?:(\+|-)(\d+)\1)?$/
const solrTimeRe = new RegExp(
  '^' + 'NOW/' + solrUnitsGroup + '(?:' + '(\\+|-)' + '(\\d+)' + '\\1' + ')?' + '$',
);

export function isValidSolrTime(t: string): boolean {
  return solrTimeRe.test(t);
}

// parseSolrTime parses a Solr time string and returns the duration relative to NOW.
// The input argument must pass `isValidSolrTime`.
export function parseSolrTime(t: string): Duration {
  const [baseSolrUnit, sign, adjustScalar] = solrTimeRe.exec(t).slice(1) as [
    SolrUnit,
    '+' | '-',
    string,
  ];
  const adjustExists = sign != null && adjustScalar != null;

  let durationScalar = 0;
  if (adjustExists) {
    const a = parseInt(adjustScalar);
    durationScalar = sign == '+' ? durationScalar + a : durationScalar - a;
  }

  return {
    unit: toDurationUnit(baseSolrUnit),
    scalar: durationScalar,
  };
}

export function toSolrUnit(d: DurationUnit): SolrUnit {
  switch (d) {
    case DurationUnit.milli:
      return SolrUnit.milli;
    case DurationUnit.second:
      return SolrUnit.second;
    case DurationUnit.minute:
      return SolrUnit.minute;
    case DurationUnit.hour:
      return SolrUnit.hour;
    case DurationUnit.day:
      return SolrUnit.day;
  }
  assertExhaustive(d);
}

function toDurationUnit(s: SolrUnit): DurationUnit {
  switch (s) {
    case SolrUnit.milli:
      return DurationUnit.milli;
    case SolrUnit.second:
      return DurationUnit.second;
    case SolrUnit.minute:
      return DurationUnit.minute;
    case SolrUnit.hour:
      return DurationUnit.hour;
    case SolrUnit.day:
      return DurationUnit.day;
  }
  assertExhaustive(s);
}
